import axios from "axios";
import { humanizeError } from "@/lib/errors";

// Default to relative URL when env var is missing — most install.sh setups
// proxy /api/ on the same domain, so a relative path always works. Without
// this guard a missing .env.production would yield "undefined/api/..." URLs
// that hit the SPA fallback (returning index.html) and silently break every
// data fetch.
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
export const API = `${BACKEND_URL}/api`;

const api = axios.create({ baseURL: API, withCredentials: true });

// Global error interceptor: every error gets a `humanMessage` property
// that callers can surface directly via toast.error(e.humanMessage).
api.interceptors.response.use(
  (resp) => resp,
  (error) => {
    try {
      error.humanMessage = humanizeError(error);
    } catch {
      error.humanMessage = "Une erreur est survenue.";
    }
    return Promise.reject(error);
  }
);

export const leadsApi = {
  list: (projet_id = null, opts = {}) => {
    const params = {};
    if (projet_id) params.projet_id = projet_id;
    if (opts.mine) params.mine = true;
    // v2.4.0 — pagination cliente facultative. Si limit/offset passés,
    // les transmet ; sinon comportement legacy (jusqu'à 1000 leads d'un coup).
    if (opts.limit) params.limit = opts.limit;
    if (opts.offset) params.offset = opts.offset;
    return api.get("/leads", { params }).then((r) => r.data);
  },
  /**
   * v2.4.0 — Variant paginée qui retourne {items, total, offset, limit,
   * has_more}. Idéal pour le "Charger plus" UI.
   */
  page: (projet_id = null, opts = {}) => {
    const params = { limit: opts.limit || 200, offset: opts.offset || 0 };
    if (projet_id) params.projet_id = projet_id;
    if (opts.mine) params.mine = true;
    return api.get("/leads/page", { params }).then((r) => r.data);
  },
  get: (id) => api.get(`/leads/${id}`).then((r) => r.data),
  create: (payload) => api.post("/leads", payload).then((r) => r.data),
  update: (id, payload) => api.put(`/leads/${id}`, payload).then((r) => r.data),
  remove: (id) => api.delete(`/leads/${id}`).then((r) => r.data),
  setAiAgent: (id, paused, reason = "") =>
    api
      .post(`/leads/${id}/ai-agent`, { paused, reason })
      .then((r) => r.data),
  confirmAiRdv: (id, rdvAt, rdvKind) =>
    api
      .post(`/leads/${id}/rdv-proposal`, {
        action: "confirm",
        rdv_at: rdvAt,
        rdv_kind: rdvKind,
      })
      .then((r) => r.data),
  rejectAiRdv: (id) =>
    api
      .post(`/leads/${id}/rdv-proposal`, { action: "reject" })
      .then((r) => r.data),
  addAction: (id, payload) =>
    api.post(`/leads/${id}/actions`, payload).then((r) => r.data),
  deleteHistory: (leadId, entryId) =>
    api.delete(`/leads/${leadId}/historique/${entryId}`).then((r) => r.data),
  updateProcedure: (id, key, value) =>
    api.patch(`/leads/${id}/procedure`, { key, value }).then((r) => r.data),
  updateProgramme: (id, jour, index, fait) =>
    api
      .patch(`/leads/${id}/programme`, { jour, index, fait })
      .then((r) => r.data),
  importLeads: (leads, skip_duplicates = true) =>
    api
      .post("/leads/import", { leads, skip_duplicates })
      .then((r) => r.data),
  stats: (projet_id = null) => {
    const params = projet_id ? { projet_id } : {};
    return api.get("/stats", { params }).then((r) => r.data);
  },
  seed: () => api.post("/seed").then((r) => r.data),
};

export const settingsApi = {
  get: () => api.get("/settings").then((r) => r.data),
  update: (payload) => api.put("/settings", payload).then((r) => r.data),
  /**
   * Per-project settings — overrides on top of the global singleton.
   * `effective` is what the runtime actually uses (merge of overrides +
   * global defaults). `overrides` is what's stored ON the project (so the
   * UI knows what's customised vs inherited).
   */
  getForProject: (projetId) =>
    api.get(`/projects/${encodeURIComponent(projetId)}/settings`).then((r) => r.data),
  updateForProject: (projetId, payload) =>
    api.put(`/projects/${encodeURIComponent(projetId)}/settings`, payload).then((r) => r.data),
  resetProject: (projetId) =>
    api.delete(`/projects/${encodeURIComponent(projetId)}/settings`).then((r) => r.data),
};

export const dashboardApi = {
  today: ({ projetId } = {}) =>
    api
      .get("/dashboard/today", { params: projetId ? { projet_id: projetId } : {} })
      .then((r) => r.data),
};

export const calendarApi = {
  appointments: ({ start, end, projetId } = {}) =>
    api
      .get("/calendar/appointments", {
        params: {
          ...(start && { start }),
          ...(end && { end }),
          ...(projetId && { projet_id: projetId }),
        },
      })
      .then((r) => r.data),
};

export const messageTemplatesApi = {
  list: () => api.get("/messages/templates").then((r) => r.data),
  preview: (payload) =>
    api.post("/messages/templates/preview", payload).then((r) => r.data),
};

export const welcomeApi = {
  // Sends the FULL welcome (text + selected documents) to an arbitrary
  // phone, exactly like the auto-pipeline would for a new lead.
  testSend: ({ phone, name, projetId = null }) =>
    api
      .post("/welcome/test-send", {
        phone,
        name,
        ...(projetId ? { projet_id: projetId } : {}),
      })
      .then((r) => r.data),
};

export const followupApi = {
  testSend: ({ phone, name }) =>
    api
      .post("/followup/test-send", { phone, name })
      .then((r) => r.data),
};

// v2.5.0 — IA composer (Suggérer une réponse). Le backend appelle Claude avec
// le full transcript du lead et retourne N variantes. Toutes les erreurs côté
// LLM sont avalées par le backend (suggestions: []) → le bouton n'a pas besoin
// de try/catch pour distinguer "AI down" vs "rien à dire".
export const aiApi = {
  suggestReply: (leadId, n = 3) =>
    api.post("/ai/suggest-reply", { lead_id: leadId, n }).then((r) => r.data),
};

export const bookingApi = {
  // Public — used by the BookingPage (the lead, no auth).
  // We bypass the `api` axios instance so the cookie isn't sent on a public
  // endpoint (cleaner — no JWT needed and avoids CORS preflight subtleties).
  publicInfo: (token) =>
    fetch(`${API}/public/booking/${encodeURIComponent(token)}/info`)
      .then(async (r) => {
        if (!r.ok) {
          const data = await r.json().catch(() => ({}));
          throw new Error(data.detail || `HTTP ${r.status}`);
        }
        return r.json();
      }),
  publicReserve: (token, slot_iso) =>
    fetch(`${API}/public/booking/${encodeURIComponent(token)}/reserve`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ slot_iso }),
    }).then(async (r) => {
      if (!r.ok) {
        const data = await r.json().catch(() => ({}));
        throw new Error(data.detail || `HTTP ${r.status}`);
      }
      return r.json();
    }),
  // Private — admin generates and sends the link.
  sendLinkToLead: (leadId, payload = {}) =>
    api.post(`/leads/${leadId}/send-booking-link`, payload).then((r) => r.data),
};

export const scheduledTasksApi = {
  list: ({ status = "pending", kind, limit = 200, projetId = null } = {}) =>
    api
      .get("/scheduled-tasks", {
        params: { status, kind, limit, ...(projetId ? { projet_id: projetId } : {}) },
      })
      .then((r) => r.data),
  forLead: (leadId) =>
    api.get(`/leads/${leadId}/scheduled-tasks`).then((r) => r.data),
  cancel: (taskId) =>
    api.post(`/scheduled-tasks/${taskId}/cancel`).then((r) => r.data),
  windowStatus: () =>
    api.get("/automation/window-status").then((r) => r.data),
};

// ---- Lead assignment (v2.2.0) -------------------------------------------
export const assignLeadTo = (leadId, userId) =>
  api
    .patch(`/leads/${encodeURIComponent(leadId)}/assign`, { user_id: userId || null })
    .then((r) => r.data);

// ---- Sequences (v2.3.0) -------------------------------------------------
export const sequencesApi = {
  list: (projet_id = null) => {
    const params = projet_id ? { projet_id } : {};
    return api.get("/sequences", { params }).then((r) => r.data);
  },
  create: (payload) => api.post("/sequences", payload).then((r) => r.data),
  update: (id, payload) =>
    api.put(`/sequences/${encodeURIComponent(id)}`, payload).then((r) => r.data),
  remove: (id) =>
    api.delete(`/sequences/${encodeURIComponent(id)}`).then((r) => r.data),
  forLead: (leadId) =>
    api
      .get(`/leads/${encodeURIComponent(leadId)}/sequences`)
      .then((r) => r.data),
  startForLead: (leadId, sequenceId) =>
    api
      .post(`/leads/${encodeURIComponent(leadId)}/sequences/start`, {
        sequence_id: sequenceId,
      })
      .then((r) => r.data),
  stopInstance: (instanceId) =>
    api
      .post(`/lead-sequences/${encodeURIComponent(instanceId)}/stop`)
      .then((r) => r.data),
};

export const projectsApi = {
  list: () => api.get("/projects").then((r) => r.data),
  // v1.8.0 — single-project read. Backend doesn't expose GET /projects/:id
  // directly so we filter the cached list. Returns null when no match.
  get: async (id) => {
    if (!id) return null;
    const items = await api.get("/projects").then((r) => r.data);
    return (items || []).find((p) => p.id === id) || null;
  },
  create: (payload) => api.post("/projects", payload).then((r) => r.data),
  update: (id, payload) => api.put(`/projects/${id}`, payload).then((r) => r.data),
  /**
   * Delete a project + cascade its leads/rappels.
   *
   * The backend requires the admin to re-type their own password before
   * the destructive cascade fires (sudo-style confirmation for sensitive
   * actions). Axios needs the password in `data` because DELETE requests
   * don't accept a body via the second positional argument.
   */
  remove: (id, { password = "" } = {}) =>
    api.delete(`/projects/${id}`, { data: { password } }).then((r) => r.data),
  reset: (id) => api.post(`/projects/${id}/reset`).then((r) => r.data),
};

export const programmesApi = {
  list: ({ activeOnly = false } = {}) => {
    const params = activeOnly ? { active_only: true } : {};
    return api.get("/programmes", { params }).then((r) => r.data);
  },
  create: (payload) => api.post("/programmes", payload).then((r) => r.data),
  update: (id, payload) => api.put(`/programmes/${id}`, payload).then((r) => r.data),
  remove: (id, { cascade = "clear" } = {}) =>
    api.delete(`/programmes/${id}`, { params: { cascade } }).then((r) => r.data),
  migrate: () => api.post("/programmes/migrate").then((r) => r.data),
  uploadCatalog: (id, file) => {
    const fd = new FormData();
    fd.append("file", file);
    return api
      .post(`/programmes/${id}/catalog`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data);
  },
  deleteCatalog: (id) =>
    api.delete(`/programmes/${id}/catalog`).then((r) => r.data),
  // Use the full backend URL so window.open() works in dev (where the
  // frontend is on a different port). In prod the same-origin nginx proxy
  // strips the leading host so relative URLs would also work — but the
  // absolute one stays correct in both modes.
  catalogUrl: (id) => `${API}/programmes/${encodeURIComponent(id)}/catalog`,
  sendCatalogToLead: (leadId, payload = {}) =>
    api.post(`/leads/${leadId}/send-catalog`, payload).then((r) => r.data),
  sendLinksToLead: (leadId, payload = {}) =>
    api.post(`/leads/${leadId}/send-links`, payload).then((r) => r.data),
};

export const documentsApi = {
  /**
   * Pass `projetId` to scope to docs owned by that project (plus globals
   * if `includeGlobal` is true, default). Without `projetId` returns
   * everything (legacy behaviour).
   */
  list: ({ autoOnly = false, projetId = null, includeGlobal = true } = {}) => {
    const params = {};
    if (autoOnly) params.auto_only = true;
    if (projetId) {
      params.projet_id = projetId;
      params.include_global = includeGlobal;
    }
    return api.get("/documents", { params }).then((r) => r.data);
  },
  upload: ({ file, nom, description, auto_send_on_new_lead, caption, projetId = null }) => {
    const fd = new FormData();
    fd.append("file", file);
    if (nom) fd.append("nom", nom);
    if (description) fd.append("description", description);
    if (caption) fd.append("caption", caption);
    fd.append("auto_send_on_new_lead", auto_send_on_new_lead ? "true" : "false");
    if (projetId) fd.append("projet_id", projetId);
    return api
      .post("/documents", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      })
      .then((r) => r.data);
  },
  update: (id, payload) =>
    api.put(`/documents/${id}`, payload).then((r) => r.data),
  remove: (id) => api.delete(`/documents/${id}`).then((r) => r.data),

  // Direct URL still useful for <img src> embeds — returns ABSOLUTE URL so
  // window.open works even in dev (frontend on :3001, backend on :8011).
  fileUrl: (id) => `${API}/documents/${encodeURIComponent(id)}/file`,

  // Preferred way to trigger a download from a button click. Goes through
  // the axios instance so the JWT cookie is always sent (withCredentials),
  // and uses a Blob + invisible <a> so the browser actually downloads
  // instead of navigating away from the SPA.
  download: async (id, filename) => {
    const resp = await api.get(`/documents/${id}/file`, {
      responseType: "blob",
    });
    const url = window.URL.createObjectURL(resp.data);
    const a = document.createElement("a");
    a.href = url;
    a.download = filename || "document";
    document.body.appendChild(a);
    a.click();
    a.remove();
    // Defer revoke so Firefox/Safari can finish the download
    setTimeout(() => window.URL.revokeObjectURL(url), 1000);
  },

  sendToLead: (leadId, payload) =>
    api.post(`/leads/${leadId}/send-document`, payload).then((r) => r.data),
};

export const automationsApi = {
  // Pass projetId to scope rules to a single project (per-project settings).
  // Without it, returns every rule (global view).
  list: (projetId = null) => {
    const params = projetId ? { projet_id: projetId } : {};
    return api.get("/automations/rules", { params }).then((r) => r.data);
  },
  create: (payload) => api.post("/automations/rules", payload).then((r) => r.data),
  update: (id, payload) =>
    api.patch(`/automations/rules/${id}`, payload).then((r) => r.data),
  remove: (id) => api.delete(`/automations/rules/${id}`).then((r) => r.data),
  run: (id) => api.post(`/automations/rules/${id}/run`).then((r) => r.data),
  runAll: () => api.post("/automations/run_all").then((r) => r.data),
};

export const rappelsApi = {
  list: (leadId = null, upcoming = false, projet_id = null) => {
    const params = {};
    if (leadId) params.lead_id = leadId;
    if (upcoming) params.upcoming = true;
    if (projet_id) params.projet_id = projet_id;
    return api.get("/rappels", { params }).then((r) => r.data);
  },
  create: (payload) => api.post("/rappels", payload).then((r) => r.data),
  update: (id, payload) => api.patch(`/rappels/${id}`, payload).then((r) => r.data),
  remove: (id) => api.delete(`/rappels/${id}`).then((r) => r.data),
};

export const analyticsApi = {
  weekly: (weeks = 8, projet_id = null) => {
    const params = { weeks };
    if (projet_id) params.projet_id = projet_id;
    return api.get("/analytics/weekly", { params }).then((r) => r.data);
  },
  caPotentiel: (projet_id = null) => {
    const params = projet_id ? { projet_id } : {};
    return api.get("/analytics/ca_potentiel", { params }).then((r) => r.data);
  },
  conversionByProgramme: (projet_id = null) => {
    const params = projet_id ? { projet_id } : {};
    return api
      .get("/analytics/conversion_by_programme", { params })
      .then((r) => r.data);
  },
  /**
   * v2.4.0 — Comparaison période courante vs précédente. period ∈
   * { week, month, quarter, year }. Retourne {metrics: {key: {current,
   * previous, delta_pct}}}.
   */
  comparison: ({ projet_id = null, period = "month" } = {}) => {
    const params = { period };
    if (projet_id) params.projet_id = projet_id;
    return api.get("/analytics/comparison", { params }).then((r) => r.data);
  },
  performance: (projet_id = null) => {
    const params = projet_id ? { projet_id } : {};
    return api.get("/analytics/performance", { params }).then((r) => r.data);
  },
  salesKpis: (projet_id = null) => {
    const params = projet_id ? { projet_id } : {};
    return api.get("/analytics/sales_kpis", { params }).then((r) => r.data);
  },
};

export const activitiesApi = {
  recent: (limit = 30, projet_id = null) => {
    const params = { limit };
    if (projet_id) params.projet_id = projet_id;
    return api.get("/activities/recent", { params }).then((r) => r.data);
  },
};

export const notificationsApi = {
  feed: (projet_id = null) => {
    const params = projet_id ? { projet_id } : {};
    return api.get("/notifications/feed", { params }).then((r) => r.data);
  },
  // Persist dismissals server-side so they stick across browsers/devices.
  markSeen: (ids) =>
    api.post("/notifications/seen", { ids }).then((r) => r.data),
  // ids omitted/empty = restore everything
  restore: (ids = null) =>
    api
      .post("/notifications/restore", ids ? { ids } : {})
      .then((r) => r.data),
};

export const insightsApi = {
  forLead: (leadId) =>
    api.get(`/leads/${leadId}/insights`).then((r) => r.data),
  forProject: (projetId = null) => {
    const params = projetId ? { projet_id: projetId } : {};
    return api.get("/insights", { params }).then((r) => r.data);
  },
  signalsMeta: () => api.get("/signals/meta").then((r) => r.data),
};

export const coachApi = {
  nextActions: (leadId, { force = false } = {}) =>
    api
      .post(`/leads/${leadId}/ai/next-actions`, null, {
        params: force ? { force: 1 } : {},
      })
      .then((r) => r.data),
  clearCache: (leadId) =>
    api.delete(`/leads/${leadId}/ai/cache`).then((r) => r.data),
};

export const whatsappApi = {
  /**
   * Connection status for a Baileys session.
   * `sessionId` is the projet_id for project-scoped sessions; omit it (or
   * pass null/undefined) to query the "default" / legacy global session.
   */
  status: (sessionId = null) =>
    api
      .get("/whatsapp/status", { params: sessionId ? { session_id: sessionId } : {} })
      .then((r) => r.data),
  qr: (sessionId = null) =>
    api
      .get("/whatsapp/qr", { params: sessionId ? { session_id: sessionId } : {} })
      .then((r) => r.data),
  /** List every WhatsApp session known to the sidecar (default + projects). */
  listSessions: () => api.get("/whatsapp/sessions").then((r) => r.data),
  /** Start (or restart) a project-scoped session. Pairing produces a QR. */
  startSession: (sessionId) =>
    api.post(`/whatsapp/sessions/${encodeURIComponent(sessionId)}/start`).then((r) => r.data),
  stopSession: (sessionId) =>
    api.post(`/whatsapp/sessions/${encodeURIComponent(sessionId)}/stop`).then((r) => r.data),
  /** Permanently delete a session (wipes its auth dir). Cannot target default. */
  deleteSession: (sessionId) =>
    api.delete(`/whatsapp/sessions/${encodeURIComponent(sessionId)}`).then((r) => r.data),
  restart: (sessionId = null) =>
    api
      .post("/whatsapp/restart", sessionId ? { session_id: sessionId } : {})
      .then((r) => r.data),
  reset: (sessionId = null) =>
    api
      .post("/whatsapp/reset", sessionId ? { session_id: sessionId } : {})
      .then((r) => r.data),
  logout: (sessionId = null) =>
    api
      .post("/whatsapp/logout", sessionId ? { session_id: sessionId } : {})
      .then((r) => r.data),
  messages: ({ leadId, phone, limit = 100 } = {}) => {
    const params = { limit };
    if (leadId) params.lead_id = leadId;
    if (phone) params.phone = phone;
    return api.get("/whatsapp/messages", { params }).then((r) => r.data);
  },
  /**
   * Welcome sweep — admin observability + control.
   *
   *   status()      → {next_run_at, interval_minutes, leads_without_welcome, last_sweep}
   *   pending()     → list of leads still missing the welcome flag
   *   history()     → last N sweep run summaries
   *   runSweep()    → fire one tick manually (returns summary)
   *   sendOne(id)   → force-send to a single lead
   */
  welcomeSweep: {
    status: () =>
      api.get("/whatsapp/welcome-sweep/status").then((r) => r.data),
    pending: ({ projetId = null, limit = 100 } = {}) => {
      const params = { limit };
      if (projetId) params.projet_id = projetId;
      return api
        .get("/whatsapp/welcome-sweep/pending", { params })
        .then((r) => r.data);
    },
    history: ({ limit = 20 } = {}) =>
      api
        .get("/whatsapp/welcome-sweep/history", { params: { limit } })
        .then((r) => r.data),
    runSweep: () =>
      api.post("/whatsapp/welcome-sweep/run").then((r) => r.data),
    sendOne: (leadId) =>
      api
        .post(`/whatsapp/welcome-sweep/send/${encodeURIComponent(leadId)}`)
        .then((r) => r.data),
  },
  /**
   * v1.9.0 — Bulk WhatsApp send. The backend iterates server-side so
   * quiet-hours + per-project session routing + min-spacing are
   * honoured uniformly. Returns {ok, total, sent, deferred, failed,
   * skipped_no_phone, skipped_no_access, quiet_window}.
   */
  bulkSend: ({ leadIds, text, templateName, templateLanguage, templateParamKeys, documentIds }) => {
    const payload = { lead_ids: leadIds, text };
    if (templateName) {
      payload.template_name = templateName;
      payload.template_language = templateLanguage || "fr";
      payload.template_param_keys = templateParamKeys || ["prenom"];
    }
    if (Array.isArray(documentIds) && documentIds.length) {
      payload.document_ids = documentIds;
    }
    return api.post("/whatsapp/bulk-send", payload).then((r) => r.data);
  },
  /**
   * v2.0.0 — Cloud API (Meta) management endpoints. Admin-only.
   */
  cloud: {
    health: (projetId) =>
      api
        .get(`/whatsapp/cloud/health/${encodeURIComponent(projetId)}`)
        .then((r) => r.data),
    templates: (projetId) =>
      api
        .get(`/whatsapp/cloud/templates/${encodeURIComponent(projetId)}`)
        .then((r) => r.data),
  },
  /**
   * Aggregate ALL WhatsApp messages associated with a phone number.
   * Wider than `messages({phone})` — also catches messages tagged with
   * lead_id (and no phone) where the lead has this phone, plus phone_pn
   * variants from privacy LIDs. Used by the admin "lookup by phone" tool.
   */
  byPhone: (phone, { limit = 500 } = {}) =>
    api
      .get(`/whatsapp/by-phone/${encodeURIComponent(phone)}`, { params: { limit } })
      .then((r) => r.data),
  /**
   * Delete a WhatsApp message.
   *   mode="local"        → soft-delete from CRM only (lead still sees it on WA)
   *   mode="for_everyone" → revoke via WhatsApp ("delete for everyone") AND
   *                          soft-delete locally. Only works on outgoing
   *                          messages within ~1h of sending.
   */
  deleteMessage: (waId, { mode = "local" } = {}) =>
    api
      .delete(`/whatsapp/messages/${encodeURIComponent(waId)}`, { params: { mode } })
      .then((r) => r.data),
  /**
   * Delete an entire conversation (every message linked to a lead/phone).
   * `mode` is the same as `deleteMessage` — for_everyone attempts to
   * revoke every outgoing message within the ~1h WhatsApp window.
   * Returns { total, deleted, revoked, revoke_errors }.
   */
  deleteConversation: ({ leadId, phone, mode = "local" } = {}) => {
    const params = { mode };
    if (leadId) params.lead_id = leadId;
    if (phone) params.phone = phone;
    return api
      .delete("/whatsapp/conversations", { params })
      .then((r) => r.data);
  },
  inbox: (limit = 30) =>
    api.get("/whatsapp/inbox", { params: { limit } }).then((r) => r.data),
  send: (payload) => api.post("/whatsapp/send", payload).then((r) => r.data),
  /**
   * Send a media file (image / video / audio / document) over WhatsApp.
   *
   * The backend expects base64 in JSON, so we read the File via FileReader.
   * Caller passes the raw File object plus optional caption — we infer the
   * media_type from the file's MIME type (anything that isn't image/video/
   * audio falls back to "document").
   */
  sendMedia: ({ phone, leadId = null, file, caption = "", mediaType }) =>
    new Promise((resolve, reject) => {
      if (!file) return reject(new Error("file_required"));
      const reader = new FileReader();
      reader.onerror = () => reject(reader.error || new Error("read_failed"));
      reader.onload = () => {
        try {
          // reader.result is a "data:<mime>;base64,<payload>" URL
          const dataUrl = String(reader.result || "");
          const data_b64 = dataUrl.includes(",") ? dataUrl.split(",")[1] : dataUrl;
          const inferredType =
            mediaType ||
            (file.type.startsWith("image/")
              ? "image"
              : file.type.startsWith("video/")
              ? "video"
              : file.type.startsWith("audio/")
              ? "audio"
              : "document");
          api
            .post(
              "/whatsapp/send-media",
              {
                phone,
                lead_id: leadId || undefined,
                media_type: inferredType,
                data_b64,
                mime: file.type || undefined,
                filename: file.name || undefined,
                caption: caption || undefined,
              },
              { timeout: 180000 } // media upload can be slow
            )
            .then((r) => resolve(r.data))
            .catch(reject);
        } catch (e) {
          reject(e);
        }
      };
      reader.readAsDataURL(file);
    }),
  /** URL the browser can use as <img src> / <a href> for an outgoing media. */
  mediaUrl: (waId) => `/api/whatsapp/media/${encodeURIComponent(waId)}`,
  markRead: (payload) => api.post("/whatsapp/mark-read", payload).then((r) => r.data),
  /** Manually associate a WhatsApp conversation (typically a LID) with a lead */
  linkLead: ({ phone, leadId }) =>
    api
      .post("/whatsapp/link-lead", { phone, lead_id: leadId })
      .then((r) => r.data),
  unlinkLead: (phone) =>
    api.post("/whatsapp/unlink-lead", { phone }).then((r) => r.data),
  /** Override the real phone number for a privacy LID conversation */
  setPhonePn: ({ phone, phonePn }) =>
    api
      .post("/whatsapp/set-phone-pn", { phone, phone_pn: phonePn })
      .then((r) => r.data),
  restart: () => api.post("/whatsapp/restart").then((r) => r.data),
  // Force a fresh pairing — wipes auth + restarts. Use when /restart is stuck
  // looping on "Connection Failure" because the stored session is invalid.
  reset: () => api.post("/whatsapp/reset").then((r) => r.data),
  logout: () => api.post("/whatsapp/logout").then((r) => r.data),
};

export const recommendationsApi = {
  nextActions: (limit = 8, projet_id = null) => {
    const params = { limit };
    if (projet_id) params.projet_id = projet_id;
    return api
      .get("/recommendations/next_actions", { params })
      .then((r) => r.data);
  },
  getBrief: (projet_id = null) => {
    const params = projet_id ? { projet_id } : {};
    return api
      .get("/recommendations/morning_brief", { params })
      .then((r) => r.data);
  },
  generateBrief: (projet_id = null, force = false) => {
    const params = {};
    if (projet_id) params.projet_id = projet_id;
    if (force) params.force = "1";
    return api
      .post("/recommendations/morning_brief", null, { params })
      .then((r) => r.data);
  },
};

// v2.7.0 — Audit log read API. Admin only ; le backend renvoie 403 sinon.
// Params : event (csv), user_id, projet_id, lead_id, since/until (ISO),
// limit/offset. Réponse : {items, total, has_more, limit, offset}.
export const auditApi = {
  list: (params = {}) =>
    api.get("/audit-log", { params }).then((r) => r.data),
  eventTypes: () => api.get("/audit-log/events").then((r) => r.data),
};

export const authApi = {
  login: (email, password) =>
    api.post("/auth/login", { email, password }).then((r) => r.data),
  logout: () => api.post("/auth/logout").then((r) => r.data),
  me: () => api.get("/auth/me").then((r) => r.data),
  changePassword: (current_password, new_password) =>
    api
      .post("/auth/change-password", { current_password, new_password })
      .then((r) => r.data),
  /** Set / clear the active programme. Pass "" to clear (admin only). */
  selectProgramme: (programme) =>
    api.post("/auth/select-programme", { programme: programme || "" }).then((r) => r.data),
  // Admin user management
  listUsers: () => api.get("/auth/users").then((r) => r.data),
  createUser: (payload) => api.post("/auth/users", payload).then((r) => r.data),
  updateUser: (id, payload) =>
    api.patch(`/auth/users/${id}`, payload).then((r) => r.data),
  deleteUser: (id) => api.delete(`/auth/users/${id}`).then((r) => r.data),
};

export const googleApi = {
  status: () => api.get("/google/status").then((r) => r.data),
  getConfig: () => api.get("/google/config").then((r) => r.data),
  saveConfig: (payload) =>
    api.post("/google/config", payload).then((r) => r.data),
  deleteConfig: () => api.delete("/google/config").then((r) => r.data),
  authStart: () => api.get("/google/auth/start").then((r) => r.data),
  disconnect: () => api.post("/google/disconnect").then((r) => r.data),
  listSheets: () => api.get("/google/sheets").then((r) => r.data),
  listTabs: (spreadsheetId) =>
    api.get(`/google/sheets/${spreadsheetId}/tabs`).then((r) => r.data),
  preview: (spreadsheetId, range) =>
    api
      .get(`/google/sheets/${spreadsheetId}/preview`, { params: { range } })
      .then((r) => r.data),
  importSheet: (spreadsheet_id, range, skip_duplicates = true) =>
    api
      .post("/google/import", { spreadsheet_id, range, skip_duplicates })
      .then((r) => r.data),
  listSyncs: () => api.get("/google/syncs").then((r) => r.data),
  createSync: (payload) =>
    api.post("/google/syncs", payload).then((r) => r.data),
  updateSync: (id, payload) =>
    api.patch(`/google/syncs/${id}`, payload).then((r) => r.data),
  deleteSync: (id) => api.delete(`/google/syncs/${id}`).then((r) => r.data),
  runSync: (id) => api.post(`/google/syncs/${id}/run`).then((r) => r.data),
};

export default api;
